import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export interface BreadcrumbItem {
  /** Label visível e usado no schema */
  label: string;
  /** Caminho relativo (ex: "/blog"). Omitir no último item (página atual) */
  href?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  /** Classes opcionais para o <nav> wrapper */
  className?: string;
  /** Classe para o item ativo (último). Default: text-gold */
  activeClassName?: string;
  /** Domínio canônico do site usado para gerar URLs absolutas no schema */
  siteUrl?: string;
}

const ChevronIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

/**
 * Breadcrumbs semânticos com Schema.org BreadcrumbList JSON-LD.
 * Renderiza a navegação visual + injeta o schema estruturado para SEO.
 */
const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  className = 'mb-7',
  activeClassName = 'text-gold font-medium line-clamp-1',
  siteUrl = 'https://luisbrunello.com.br',
}) => {
  if (!items || items.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      ...(item.href ? { item: `${siteUrl}${item.href}` } : {}),
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <nav aria-label="breadcrumb" className={className}>
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-light-muted/60 list-none p-0 m-0">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            return (
              <React.Fragment key={`${item.label}-${index}`}>
                <li className={isLast ? activeClassName : ''}>
                  {isLast || !item.href ? (
                    <span aria-current={isLast ? 'page' : undefined}>{item.label}</span>
                  ) : (
                    <Link to={item.href} className="hover:text-light-primary transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
                {!isLast && (
                  <li aria-hidden="true" className="flex items-center">
                    <ChevronIcon />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;
